import { privateApi } from '@shared/config/axios.config';

import { parseModuleDTO, parseModulesWithPlans } from '../lib/parsers';
import { Module, ModuleLevel, ModulePrice, ModuleType, SyllabusSection } from '../model/types';
import { AssessmentReport } from '../model/report';

export interface ModuleDTO {
  id: string;
  title: string;
  description: string;
  level: ModuleLevel;
  is_published: boolean;
  syllabus: SyllabusDTO[] | null;
  quizes: QuizMetaDTO[] | null;
  image_url?: string;
  owner_id?: string;
  rating?: number;
  created_at?: string;
  updated_at?: string;
  stats?: ModuleStatsDTO;
  price?: ModulePriceDTO;
  subscription_price?: ModulePriceDTO;
  full_payment_price?: ModulePriceDTO;
  videos: VideoDTO[] | null;
  plans?: PlanDTO[] | null;
  modules?: ModuleDTO[] | null;
  playground_files?: PlaygroundFileDTO[] | null;
  plan_order?: number;
  has_trial?: boolean;
  payment_type: ModuleType;
  next_payment_time?: string | null;
}

interface PlaygroundFileDTO {
  file_name: string;
  link: string;
}

interface ModulePriceDTO {
  price_id: string;
  amount: number;
  currency: string;
  region: string;
  tax_amount: number;
  tax_percentage: number;
}

export interface PlanDTO {
  id: string;
  title: string;
  image_url: string | null;
  next_module_id: string | null;
  certificate_url: string | null;
  is_published?: boolean | null;
}

interface SyllabusDTO {
  id?: string;
  title: string;
  order: number;
  sections_titles_with_status?: SyllabusSection[] | null;
  sections_titles_sorted?: string[] | null;
  stats?: SyllabusStatsDTO;
}

interface SyllabusStatsDTO {
  total_count: number;
  completed_count: number;
  completed: boolean;
}

interface QuizMetaDTO {
  id?: string;
  title: string;
  order: number;
  completed?: boolean;
  failed?: boolean;
  score?: number;
  locked?: boolean;
}

interface ModuleStatsDTO {
  quiz_total: number;
  quiz_completed: number;
  articles_total: number;
  articles_completed: number;
  lesson_total: number;
  lesson_completed: number;
  progress: number;
  completed: boolean;
  failed?: boolean;
  video_completed: number;
  video_total: number;
  assessment_report?: AssessmentReport;
}

interface VideoDTO {
  id: string;
  title: string;
  description: string;
  order: number;
  bunny_video_url: string;
  bunny_perview_url: string;
  completed: boolean;
}

interface GetModulesResponse {
  modules: ModuleDTO[] | null;
  recommendations: ModuleDTO[] | null;
  all_plans: ModuleDTO[] | null;
  overall_progress: number;
  jupiter_base_url: string;
  trial_payment_needed: boolean;
  trial_price?: ModulePriceDTO | null;
}

export const getModulesApi = () => {
  return privateApi.get<GetModulesResponse>('/account/content/access').then(({ data }) => ({
    modulesByPlan: data.modules ? parseModulesWithPlans(data.modules) : new Map<string, Module[]>(),
    recommendations: data.recommendations?.map((dto) => parseModuleDTO(dto, undefined, true)) || [],
    plans: data.all_plans?.map((dto) => parseModuleDTO(dto, undefined, true)) || [],
    overallProgress: data.overall_progress,
    jupiterBaseUrl: data.jupiter_base_url,
    trialPaymentNeeded: data.trial_payment_needed,
    trialPrice: data.trial_price
      ? ({
          priceId: data.trial_price.price_id,
          amount: data.trial_price.amount,
          currency: data.trial_price.currency,
          region: data.trial_price.region,
          taxAmount: data.trial_price.tax_amount,
          taxPercentage: data.trial_price.tax_percentage,
        } as ModulePrice)
      : undefined,
  }));
};

interface CompleteVideoPayload {
  module_id: string;
  video_id: string;
}

export const completeVideoApi = (payload: CompleteVideoPayload) => {
  return privateApi.put('/account/progress/video', payload);
};
